import type { MaybeRef, Reactive } from "vue"
import type { ServerResponseV1 } from "@/http"
import type { IAlbumResponseV1 } from "@/types"
import { useQuery } from "@tanstack/vue-query"
import { ref, toValue } from "vue"
import { v1 } from "@/axios"
import { Album } from "@/models"

export interface IListAlbumsQueryParams {
  page?: number
  perPage?: number
  search?: string
}

export async function listAlbums(eventVaultId: number, { page, perPage, search }: IListAlbumsQueryParams = {}) {
  const query = new URLSearchParams()

  if (page)
    query.append("page", page.toString())
  if (perPage)
    query.append("perPage", perPage.toString())
  if (search)
    query.append("search", search)

  const response = await v1.get<ServerResponseV1<IAlbumResponseV1[]>>(`/events/${eventVaultId}/albums?${query.toString()}`)

  return {
    albums: response.data.data?.map(album => Album.fromResponseV1(album)) ?? [],
    meta: response.data.meta,
    purchaseLink: response.data.meta?.extensions?.albumPurchaseLink,
  }
}

export const LIST_ALBUMS_QUERY_KEY = "listAlbums"

export function useListAlbums(eventVaultId: MaybeRef<number | undefined>, params: Reactive<IListAlbumsQueryParams> = {}) {
  const pageCount = ref<number>(0)
  const purchaseLink = ref<string>("")

  const query = useQuery({
    queryKey: [LIST_ALBUMS_QUERY_KEY, eventVaultId, params],
    queryFn: async () => {
      const response = await listAlbums(toValue(eventVaultId)!, params)

      if (response.meta?.totalPages) {
        pageCount.value = Number(response.meta.totalPages)
      }

      if (response.purchaseLink && typeof response.purchaseLink === "string") {
        purchaseLink.value = response.purchaseLink
      }

      return response.albums
    },
    enabled: !!toValue(eventVaultId),
    retry: false,
  })

  return {
    ...query,
    pageCount,
    purchaseLink,
  }
}
